import { useState, useEffect, useRef } from "react";
import mockTargetFields from "../../../../mocks/targetFields.json";
import { convertApiResponseToUiData, createDefaultFieldMappings } from "../utils/apiToUiConverter";

export const useFieldMappings = (activeProfile, fetchFieldMappingDetail) => {
  const [fieldMappings, setFieldMappings] = useState([]);
  const [isEmptyMapping, setEmptyMapping] = useState(false);
  const [picklistMappings, setPicklistMappings] = useState({});
  const [pipelinePicklistMappings, setPipelinePicklistMappings] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Store duplicated data to prevent fetch override
  const duplicatedDataRef = useRef(null);

  // Timeout ref to force stop loading after max wait time
  const loadingTimeoutRef = useRef(null);

  useEffect(() => {
    // Clear any existing timeout when effect runs
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }

    // If we have duplicated data for this profile, use it instead of fetching
    if (duplicatedDataRef.current && duplicatedDataRef.current.profileId === activeProfile) {
      setFieldMappings(duplicatedDataRef.current.fieldMappings);
      setPicklistMappings(duplicatedDataRef.current.picklistMappings);
      setPipelinePicklistMappings(duplicatedDataRef.current.pipelinePicklistMappings || {});
      setEmptyMapping(duplicatedDataRef.current.fieldMappings.length === 0);
      duplicatedDataRef.current = null; // Clear after using
      setIsLoading(false);
      return;
    }

    // If no active profile, reset loading state
    if (!activeProfile) {
      setIsLoading(false);
      return;
    }

    if (activeProfile && fetchFieldMappingDetail) {
      setIsLoading(true);

      // Set timeout to force stop loading after 10 seconds
      loadingTimeoutRef.current = setTimeout(() => {
        console.warn("API call timeout - forcing loading state to false");
        setIsLoading(false);
        setFieldMappings(createDefaultFieldMappings(mockTargetFields));
        setPicklistMappings({});
      }, 10000); // 10 seconds timeout

      try {
        fetchFieldMappingDetail(activeProfile, (apiResponse) => {
          // Clear timeout since we got response
          if (loadingTimeoutRef.current) {
            clearTimeout(loadingTimeoutRef.current);
            loadingTimeoutRef.current = null;
          }

          if (apiResponse && apiResponse.status === "ok") {
            const {
              fieldMappings: convertedMappings,
              picklistMappings: convertedPicklistMappings,
              pipelinePicklistMappings: convertedPipelinePicklistMappings
            } = convertApiResponseToUiData(apiResponse, mockTargetFields);

            // If converted mappings are empty, use default mappings
            if (convertedMappings.length === 0) {
              console.log("No field mappings from API, using default template");
              setFieldMappings(createDefaultFieldMappings(mockTargetFields));
              setPicklistMappings({});
              setPipelinePicklistMappings({});
              setEmptyMapping(true);
            } else {
              setFieldMappings(convertedMappings);
              setPicklistMappings(convertedPicklistMappings);
              setPipelinePicklistMappings(convertedPipelinePicklistMappings || {});
              setEmptyMapping(false);
            }
          } else {
            // Handle error or empty response - use default field mappings
            console.log("API error or no response, using default field mappings");
            setFieldMappings(createDefaultFieldMappings(mockTargetFields));
            setPicklistMappings({});
            setPipelinePicklistMappings({});
            setEmptyMapping(true);
          }
          setIsLoading(false);
        });
      } catch (error) {
        // Catch any synchronous errors
        console.error("Error calling fetchFieldMappingDetail:", error);
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
          loadingTimeoutRef.current = null;
        }
        setIsLoading(false);
        setFieldMappings(createDefaultFieldMappings(mockTargetFields));
        setPicklistMappings({});
        setPipelinePicklistMappings({});
      }
    } else if (activeProfile && !fetchFieldMappingDetail) {
      // If no fetch function provided (e.g., new profile), use default mappings
      console.log("No fetch function, using default field mappings");
      const defaultMappings = createDefaultFieldMappings(mockTargetFields);
      setFieldMappings(defaultMappings);
      setPicklistMappings({});
      setPipelinePicklistMappings({});
      setEmptyMapping(defaultMappings.length === 0);
      setIsLoading(false);
    }

    // Cleanup timeout on unmount or when dependencies change
    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
    };
  }, [activeProfile, fetchFieldMappingDetail]);

  const handleMappingChange = (targetFieldApiName, sourceFieldApiName) => {
    // Capture old source field value to check if it changed
    let oldSourceFieldApiName = null;

    setFieldMappings((prev) => {
      const oldMapping = prev.find(m => m.targetField.apiName === targetFieldApiName);
      oldSourceFieldApiName = oldMapping ? oldMapping.sourceFieldApiName : null;

      return prev.map((mapping) =>
        mapping.targetField.apiName === targetFieldApiName
          ? { ...mapping, sourceFieldApiName, isSaved: false }
          : mapping
      );
    });

    // Reset picklist mappings when source field changes
    if (oldSourceFieldApiName !== sourceFieldApiName) {
      // Reset regular picklist mappings
      setPicklistMappings((prev) => {
        const updated = { ...prev };
        delete updated[targetFieldApiName];
        return updated;
      });

      // Reset pipeline picklist mappings for all pipelines
      setPipelinePicklistMappings((prev) => {
        const updated = {};
        Object.keys(prev).forEach((pipelineId) => {
          if (prev[pipelineId]) {
            const pipelineMappings = { ...prev[pipelineId] };
            delete pipelineMappings[targetFieldApiName];
            updated[pipelineId] = pipelineMappings;
          }
        });
        return updated;
      });
    }
  };

  const handleDefaultValueChange = (targetFieldApiName, defaultValue) => {
    setFieldMappings((prev) =>
      prev.map((mapping) =>
        mapping.targetField.apiName === targetFieldApiName
          ? { ...mapping, defaultValue, isSaved: false }
          : mapping
      )
    );
  };

  const handlePicklistMappingsSave = (saveData) => {
    const { targetFieldName, mappings, pipelineId, hasPipelines, currentPipelineId, currentMappings, allPipelineMappings } = saveData;

    if (hasPipelines && allPipelineMappings) {
      // Save all pipelines mappings at once (from temp storage in modal)
      setPipelinePicklistMappings(allPipelineMappings);
    } else if (hasPipelines && currentPipelineId) {
      // Legacy: Save to pipeline-specific mappings for the current pipeline only
      setPipelinePicklistMappings((prev) => ({
        ...prev,
        [currentPipelineId]: {
          ...prev[currentPipelineId],
          [targetFieldName]: currentMappings,
        },
      }));
    } else if (pipelineId) {
      // Legacy: Save to pipeline-specific mappings
      setPipelinePicklistMappings((prev) => ({
        ...prev,
        [pipelineId]: {
          ...prev[pipelineId],
          [targetFieldName]: mappings,
        },
      }));
    } else {
      // Save to regular picklist mappings
      setPicklistMappings((prev) => ({
        ...prev,
        [targetFieldName]: mappings,
      }));
    }
  };

  // Prepare duplicated data for a specific profile (called before switching to new profile)
  const prepareDuplicatedData = (profileId, newFieldMappings, newPicklistMappings, newPipelinePicklistMappings) => {
    duplicatedDataRef.current = {
      profileId,
      fieldMappings: newFieldMappings,
      picklistMappings: newPicklistMappings,
      pipelinePicklistMappings: newPipelinePicklistMappings || {},
    };
  };

  return {
    fieldMappings,
    picklistMappings,
    pipelinePicklistMappings,
    handleMappingChange,
    handleDefaultValueChange,
    handlePicklistMappingsSave,
    prepareDuplicatedData,
    isLoading,
    isEmptyMapping
  };
};
